/**
 * JSON Schema validation utilities for frontend
 * Uses ajv (https://github.com/ajv-validator/ajv) for validation
 * Schemas are loaded from shared source of truth
 */

import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import ajvErrors from 'ajv-errors';
import { budgetSchemas, definitions, schemas } from '../schemas';

// Initialize AJV with options
const ajv = new Ajv({ 
  allErrors: true,
  verbose: true,
  strict: false,
  validateFormats: true
});

// Add format validators (uuid, date, etc.)
addFormats(ajv);
// Add better error messages
ajvErrors(ajv);

// Add the full schema with definitions for $ref resolution
ajv.addSchema(budgetSchemas, budgetSchemas.$id);

// Add individual definitions to enable reference resolution
Object.entries(definitions).forEach(([name, schema]) => {
  ajv.addSchema(schema, `#/definitions/${name}`);
});

// Compile validators from imported schemas
// Using getSchema for schemas that were added, compile for inline schemas
const validators = {
  hourData: ajv.getSchema('#/definitions/hourDataArray') || ajv.compile({ type: 'array', items: { $ref: '#/definitions/hourDataEntry' } }),
  hourDataEntry: ajv.getSchema('#/definitions/hourDataEntry')!,
  budgetPosition: ajv.getSchema('#/definitions/budgetPositionRole')!,
  budgetPositions: schemas.budgetPositions ? ajv.compile(schemas.budgetPositions) : ajv.compile({ type: 'array', items: { $ref: '#/definitions/budgetCategory' } }),
  externalPosition: ajv.getSchema('#/definitions/externalPosition')!,
  externalPositions: schemas.externalPositions ? ajv.compile(schemas.externalPositions) : ajv.compile({ type: 'array', items: { $ref: '#/definitions/externalPosition' } }),
  calculationFigures: schemas.calculationFigures ? ajv.compile(schemas.calculationFigures) : ajv.getSchema('#/definitions/calculationFigures')!,
  unitHoursData: schemas.unitHoursData ? ajv.compile(schemas.unitHoursData) : ajv.getSchema('#/definitions/unitHoursData')!,
  billingRate: ajv.getSchema('#/definitions/billingRate') || ajv.compile({ type: 'object' }),
  billingRatesData: schemas.billingRatesData ? ajv.compile(schemas.billingRatesData) : ajv.compile({ type: 'array', items: { $ref: '#/definitions/billingRate' } }),
  phase: ajv.getSchema('#/definitions/phase')!,
  phases: ajv.getSchema('#/definitions/phases') || ajv.compile({ type: 'array', items: { $ref: '#/definitions/phase' } }),
  allocationEntry: ajv.getSchema('#/definitions/allocationEntry')!,
  allocationOfPatients: ajv.getSchema('#/definitions/allocationOfPatients') || ajv.compile({ type: 'array', items: { $ref: '#/definitions/allocationEntry' } })
};

// Validation result type
export interface ValidationResult {
  valid: boolean;
  errors?: Array<{
    field: string;
    message: string;
    value?: any;
  }>;
}

// User-friendly error formatter
function formatValidationErrors(errors: any[]): ValidationResult['errors'] {
  if (!errors) return undefined;
  
  return errors.map(error => {
    const field = error.instancePath 
      ? error.instancePath.replace(/^\//, '').replace(/\//g, '.')
      : 'root';
    
    let message = error.message || 'Invalid value';
    
    // Enhance error messages based on keyword
    switch (error.keyword) {
      case 'required':
        message = `${error.params.missingProperty} is required`;
        break;
      case 'type':
        message = `Must be a ${error.params.type}`;
        break;
      case 'minimum':
        message = `Must be at least ${error.params.limit}`;
        break;
      case 'maximum':
        message = `Must be at most ${error.params.limit}`;
        break;
      case 'format':
        message = `Invalid format (expected ${error.params.format})`;
        break;
      case 'minLength':
        message = `Must be at least ${error.params.limit} characters`;
        break;
      case 'maxLength':
        message = `Must be at most ${error.params.limit} characters`;
        break;
      case 'pattern':
        message = `Invalid format`;
        if (error.schema && error.schema.includes('[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}')) {
          message = 'Must be a valid UUID';
        }
        break;
      case 'oneOf':
        message = 'Value must match one of the allowed formats';
        break;
    }
    
    // Include the actual data that caused the error
    let dataSnippet = error.data;
    
    // For large objects/arrays, truncate the display
    if (dataSnippet && typeof dataSnippet === 'object') {
      try {
        const jsonStr = JSON.stringify(dataSnippet, null, 2);
        if (jsonStr.length > 200) {
          // For large data, show first 200 chars
          dataSnippet = jsonStr.substring(0, 200) + '...';
        } else {
          dataSnippet = jsonStr;
        }
      } catch (e) {
        dataSnippet = '[Object - could not serialize]';
      }
    }
    
    return {
      field: error.keyword === 'required' ? error.params.missingProperty : (field === 'root' ? error.params.missingProperty || field : field),
      message,
      value: dataSnippet
    };
  });
}

// Main validation functions
export function validateHourData(data: any): ValidationResult {
  const valid = validators.hourData(data) as boolean;
  return {
    valid,
    errors: valid ? undefined : formatValidationErrors(validators.hourData.errors!)
  };
}

export function validateHourDataEntry(data: any): ValidationResult {
  const valid = validators.hourDataEntry(data) as boolean;
  return {
    valid,
    errors: valid ? undefined : formatValidationErrors(validators.hourDataEntry.errors!)
  };
}

export function validateBudgetPosition(data: any): ValidationResult {
  const valid = validators.budgetPosition(data) as boolean;
  return {
    valid,
    errors: valid ? undefined : formatValidationErrors(validators.budgetPosition.errors!)
  };
}

export function validateBudgetPositions(data: any): ValidationResult {
  const valid = validators.budgetPositions(data) as boolean;
  return {
    valid,
    errors: valid ? undefined : formatValidationErrors(validators.budgetPositions.errors!)
  };
}

export function validateExternalPositions(data: any): ValidationResult {
  const valid = validators.externalPositions(data) as boolean;
  return {
    valid,
    errors: valid ? undefined : formatValidationErrors(validators.externalPositions.errors!)
  };
}

export function validateCalculationFigures(data: any): ValidationResult {
  const valid = validators.calculationFigures(data) as boolean;
  return {
    valid,
    errors: valid ? undefined : formatValidationErrors(validators.calculationFigures.errors!)
  };
}

export function validateUnitHoursData(data: any): ValidationResult {
  const valid = validators.unitHoursData(data) as boolean;
  return {
    valid,
    errors: valid ? undefined : formatValidationErrors(validators.unitHoursData.errors!)
  };
}

export function validateBillingRatesData(data: any): ValidationResult {
  const valid = validators.billingRatesData(data) as boolean;
  return {
    valid,
    errors: valid ? undefined : formatValidationErrors(validators.billingRatesData.errors!)
  };
}

export function validatePhases(data: any): ValidationResult {
  const valid = validators.phases(data) as boolean;
  return {
    valid,
    errors: valid ? undefined : formatValidationErrors(validators.phases.errors!)
  };
}

export function validateAllocationOfPatients(data: any): ValidationResult {
  const valid = validators.allocationOfPatients(data) as boolean;
  return {
    valid,
    errors: valid ? undefined : formatValidationErrors(validators.allocationOfPatients.errors!)
  };
}

// React hook for validation
import { useState, useEffect } from 'react';

export function useJsonValidation<T>(
  validator: (data: any) => ValidationResult,
  data: T
): ValidationResult {
  const [result, setResult] = useState<ValidationResult>({ valid: true });
  
  useEffect(() => {
    const validationResult = validator(data);
    setResult(validationResult);
    
    if (!validationResult.valid && process.env.NODE_ENV === 'development') {
      console.warn('Validation failed:', validationResult.errors);
    }
  }, [data, validator]);
  
  return result;
}

// Validate before API call
export function validateApiPayload<T>(
  schemaName: keyof typeof validators,
  payload: T
): T {
  const validator = validators[schemaName];
  if (!validator) {
    console.warn(`No validator found for schema: ${schemaName}`);
    return payload;
  }
  
  const valid = validator(payload) as boolean;
  if (!valid) {
    const errors = formatValidationErrors(validator.errors!);
    const errorMessage = errors?.map(e => `${e.field}: ${e.message}`).join(', ');
    throw new Error(`Validation failed: ${errorMessage}`);
  }
  
  return payload;
}

// Type generation from schemas (for TypeScript)
export type HourDataEntry = {
  id: string;
  department_id: number;
  department_shortcut?: string;
  department_name?: string;
  position_id: number;
  position_shortcut?: string;
  position_name?: string;
  hours: number;
};

export type BudgetPosition = {
  id: number | string;  // Can be integer or UUID
  key: string;
  position_name: string;
  department: number;
  department_name?: string;
  unit: number | null;
  unit_name?: string;
  hour_data?: HourDataEntry[];
  total_hours?: number | string;
  hourly_rate?: number | string;
};

export type BudgetCategory = {
  id: number;
  name: string;
  display_order: number;
  department_roles?: BudgetPosition[];
};

export type ExternalPosition = {
  position_name: string;
  supplier_name?: string;
  unit_name: string;
  unit_relationship?: string;
  quantity: number;
  price_per_unit: number;
  total_cost?: number;
};

export type CalculationFigures = {
  trainings?: number;
  submissions?: number;
  onSiteVisits?: number;
  initiationVisits?: number;
  queries?: number;
  ecrf_forms?: number;
  [key: string]: number | undefined;
};

export type UnitHoursData = {
  [unitName: string]: number;
};

export type BillingRate = {
  id: number;
  department_id: number;
  department_name?: string;
  position_id: number;
  position_name?: string;
  billing_rate: number;
};

export type Phase = {
  name: string;
  timeline_days: number;
  start_date?: string;
  end_date?: string;
};

// Export for testing and debugging
export { ajv, validators, formatValidationErrors };